/** An indoor day: the structured workout (.zwo for any trainer app) and, for MyWhoosh, matching courses. */
import { download } from "@/api/client";
import { useIndoor, useSettings } from "@/api/hooks";
import { num } from "@/lib/format";
import { Card } from "./ui";
import { useToast } from "./Toast";

export function IndoorCard({ day }: { day: string }) {
  const q = useIndoor(day);
  const app = useSettings().data?.trainer_app ?? null;
  const toast = useToast();
  const d = q.data;
  if (!d || !d.session || d.session.kind === "rest") return null;
  const s = d.session;
  return (
    <Card
      title={
        app === "mywhoosh"
          ? "Indoor · MyWhoosh"
          : app === "zwift"
            ? "Indoor · Zwift"
            : app === "rouvy"
              ? "Indoor · Rouvy"
              : "Indoor"
      }
      actions={
        <button
          className="small primary"
          onClick={() =>
            download(`/plan/sessions/${s.id}/workout.zwo`, `workout-${day}.zwo`).catch((e: unknown) =>
              toast.error(e),
            )
          }
        >
          Download .zwo
        </button>
      }
    >
      <div className="stack" style={{ gap: 12 }}>
        {d.workout && (
          <div>
            <b>{d.workout.name}</b> <span className="muted">· {num(d.workout.total_minutes)} min</span>
            <div className="split" style={{ height: 22, marginTop: 6 }} aria-label="Workout structure">
              {d.workout.steps.map((st, i) => {
                const pct = (st.power_low_pct + st.power_high_pct) / 2;
                const color =
                  pct >= 0.88
                    ? "var(--bad)"
                    : pct >= 0.76
                      ? "var(--warn)"
                      : pct >= 0.56
                        ? "var(--accent)"
                        : "var(--good)";
                return (
                  <span
                    key={i}
                    title={`${st.label}: ${Math.round(st.duration_s / 60)} min @ ${Math.round(st.power_low_pct * 100)}–${Math.round(st.power_high_pct * 100)}% FTP`}
                    style={{
                      width: `${(st.duration_s / 60 / d.workout!.total_minutes) * 100}%`,
                      background: color,
                      opacity: 0.4 + Math.min(0.6, pct / 1.5),
                    }}
                  />
                );
              })}
            </div>
            <small className="faint">
              {app === "mywhoosh"
                ? "Import in MyWhoosh: workout.mywhoosh.com → Custom workouts → Import .zwo. Record on your bike computer as usual."
                : "Most trainer apps import .zwo workouts (look for custom or imported workouts)."}
            </small>
          </div>
        )}
        {app === "mywhoosh" && d.routes.length > 0 && (
          <div className="list">
            {d.routes.map((r) => (
              <div key={r.id} className="row between" style={{ fontSize: 14 }}>
                <span>
                  <b>{r.name}</b> {r.world && <span className="muted">· {r.world}</span>}
                  <br />
                  <small className="muted">{r.reason}</small>
                </span>
                <span className="mono">
                  {num(r.distance_km, 1)} km{r.laps > 1 ? ` ×${r.laps}` : ""} · {num(r.elevation_m)} m · ~
                  {num(r.est_minutes)}′
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
